import { Cart } from "@/shared/dtos/Cart";
import { API } from "../api";
import { ssmHttpService } from "../services/http-service";

export class CartHistory {
    public current: Cart;
    public history: Cart[];

    constructor(data?: Partial<CartHistory>) {
        if (data && data.current) {
            this.current = new Cart(data.current as any);
        } else {
            this.current = new Cart();
        }

        if (data && data.history && data.history.length) {
            this.history = (data.history as any[]).map(function (h) {
                return new Cart(h as any);
            });
        } else {
            this.history = [];
        }
    }
    finalizeCurrent(pushToHistory = true) {
        if (pushToHistory && this.current && this.current.items.length) {
            const payload = { current: this.current.toPlain() };
            ssmHttpService.post(API.carts+'/ingest/true', payload);
            // snapshot
            this.history.unshift(new Cart(this.current.toPlain() as any));
        }
        // nuevo carrito vacío, pero conservar client si you want; adjust as needed
        this.current = new Cart({ client: this.current.client ? { ...(this.current.client as any) } : null });
    }

    loadPlain(obj: any) {
        if (!obj) return;
        this.current = obj.current ? new Cart(obj.current) : new Cart();
        this.history = (obj.history || []).map((h: any) => new Cart(h));
    }

    toPlain() {
        return {
            current: this.current.toPlain(),
            history: this.history.map(h => h.toPlain()),
        };
    }
}

/**
 * Contenedor con carrito actual e histórico
 */
/**
 * Representa un gestor de carritos que mantiene el carrito activo y una línea de tiempo
 * de carritos anteriores.
 *
 * Contiene un `current` Cart que representa el carrito de la compra en curso y un
 * array `history` que contiene los carritos anteriores (el más reciente primero).
 *
 * Notas:
 * - Las instancias de `Cart` se construyen/copian al asignar a `current` o a las entradas
 * en `history`, por lo que la identidad del objeto no se conserva; esta clase opera sobre
 * instantáneas/valores en lugar de referencias.
 * - La serialización y deserialización utilizan objetos simples a través de `toPlain()` y `loadPlain()`.
 *
 * Propiedades:
 * - `current`: El `Cart` actualmente activo. Siempre existe (se inicializa con un nuevo `Cart` si está ausente).
 * - `history`: Un array de instancias de `Cart` anteriores ordenadas de más nuevo a más antiguo.
 *
 * @example
 * const ch = new CartHistory();
 * ch.current.addItem(...);
 * ch.finalizeCurrent(); // empuja el actual al historial y lo reemplaza con un nuevo carrito vacío
 *
 * @param data - Objeto de inicialización parcial opcional. Si se proporciona, `current` y cada
 * elemento en `history` se convertirán en instancias de `Cart`.
 *
 * @remarks
 * - El constructor acepta un Partial<CartHistory> y mapeará los objetos simples proporcionados
 * a instancias de `Cart`. Esto lo hace conveniente para hidratar desde JSON u otros datos simples.
 *
 * Métodos:
 * - finalizeCurrent(pushToHistory = true): Finaliza el carrito activo. Si `pushToHistory` es true
 * y el carrito actual contiene artículos, una instantánea (un nuevo `Cart` construido a partir de la
 * representación simple del carrito actual) se añade al principio (`unshift`) de `history`.
 * El carrito `current` se reemplaza entonces con un nuevo `Cart` vacío. La información del cliente
 * del carrito `current` anterior se conserva en el nuevo carrito si estaba presente.
 *
 * @param pushToHistory - Cuando es true (por defecto), el carrito actual con artículos se registra en `history`.
 *
 * - loadPlain(obj: any): Carga el estado desde un objeto simple (normalmente JSON parseado). Si `obj` es
 * falso (`falsy`), no se realiza ninguna acción. De lo contrario, `current` e `history` se reconstruyen
 * como instancias de `Cart` a partir de los datos simples correspondientes.
 *
 * @param obj - Objeto simple que contiene las formas opcionales de `current` e `history` que coinciden con Cart/Cart[].
 *
 * - toPlain(): Produce una representación simple serializable de CartHistory adecuada para almacenamiento
 * o transferencia. Esto devuelve un objeto con entradas `current` e `history` donde cada Cart ha sido
 * convertido a través de su método `toPlain()`.
 *
 * @returns Un objeto simple: { current: any, history: any[] } donde cada valor es el resultado de `Cart.toPlain()`.
 */